import { useContext, useEffect, useMemo, useState } from 'react'
import { Paper, Stack, Typography } from '@mui/material'
import { FactCheckOutlined } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import ReadinessService from '../../../shared/services/readiness/readiness.service'
import { IReadiness } from '../../../shared/services/readiness/readiness.model'
import { ReportPeriodContext } from '../../../context/report-periods/report-periods'
import { ReadinessStateChip } from '../readiness-components/readiness-state-chip'

// Dashboard strip for admins and analysts: the readiness state and what is outstanding, one
// click from the full page. The data-owner dashboard keeps its My Tasks card instead.
export const ReadinessCard = () => {
  const navigate = useNavigate()
  const readinessService = useMemo(() => new ReadinessService(), [])
  const reportPeriodContext = useContext(ReportPeriodContext)
  const reportPeriodId = reportPeriodContext.lastSelectedReportId
  const [readiness, setReadiness] = useState<IReadiness | null>(null)

  useEffect(() => {
    if (!reportPeriodId) return
    readinessService.getForPeriod(reportPeriodId)
      .then(setReadiness)
      .catch(err => console.log(err))
  }, [reportPeriodId, readinessService])

  // Nothing until loaded: the dashboard must not flash an empty card.
  if (!readiness) return null

  const blocking = readiness.score.integrity.blocks_reporting
  const decisions = readiness.score.integrity.needs_decision
  const summaryParts: string[] = []
  if (blocking > 0) summaryParts.push(`${blocking} critical ${blocking === 1 ? 'finding' : 'findings'}`)
  if (decisions > 0) summaryParts.push(`${decisions} ${decisions === 1 ? 'decision' : 'decisions'} outstanding`)
  const summary = summaryParts.length > 0
    ? `${summaryParts.join(', ')} for this period.`
    : (readiness.findings.length > 0
      ? `${readiness.findings.length} ${readiness.findings.length === 1 ? 'finding' : 'findings'} worth checking.`
      : 'No open findings for this period.')

  return (
    <Paper
      variant='outlined'
      onClick={() => navigate('/readiness')}
      sx={{
        p: 2.5,
        mb: 2,
        cursor: 'pointer',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        '&:hover': { borderColor: 'primary.main', boxShadow: 1 },
      }}
    >
      <Stack direction='row' alignItems='center' spacing={2}>
        <FactCheckOutlined sx={{ color: 'text.secondary' }} />
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>Report Readiness</Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>{summary}</Typography>
        </Stack>
        <ReadinessStateChip state={readiness.state} />
      </Stack>
    </Paper>
  )
}
